import { useEffect, useState } from "react";

import {
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SurveyVersionOverview } from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";
import JSZip from "jszip";

import { exportSurvey, type SurveyExportFormat } from "@/api";
import {
  extractLocaleStringFromField,
  formatVersionNumber,
} from "@/components/online-reporting/form-management/utils";
import { ILocaleObject } from "@/components/online-reporting/types";

interface ExportReportsModalProps {
  open: boolean;
  onClose: () => void;
  surveys: SurveyVersionOverview[];
  format: SurveyExportFormat;
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer so browsers that read the blob lazily still receive the bytes.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * The version an export downloads: the published one if there is one, else the
 * draft. Display and export share this so the Version column never shows one
 * thing while a different version is exported.
 */
const getExportVersion = (survey: SurveyVersionOverview) =>
  survey.active_version || survey.draft_version;

/**
 * A "New" survey has neither a published nor a draft version, so there's
 * nothing to export — it must not be selectable, or it would count as a
 * failure and never leave the retry set.
 */
const canExport = (survey: SurveyVersionOverview): boolean =>
  Boolean(getExportVersion(survey)?.id);

const getSurveyTitle = (survey: SurveyVersionOverview): string => {
  const title = getExportVersion(survey)?.configuration?.title;
  const resolved = title
    ? extractLocaleStringFromField(title as string | ILocaleObject)
    : "";
  return resolved || "Untitled Survey";
};

const surveyFilename = (title: string, format: SurveyExportFormat): string => {
  const slug =
    title
      .normalize("NFKD") // split accented letters into base + combining mark
      .replace(/[\u0300-\u036f]/g, "") // drop the marks: é → e, not "_"
      .replace(/[^a-z0-9]+/gi, "_") // collapse non-alphanumeric runs to "_"
      .replace(/^_+|_+$/g, "") || "survey_summary";
  return `${slug}.${format === "pdf" ? "pdf" : "md"}`;
};

/**
 * Two surveys can share a title (e.g. "Incident Report"), which would collide
 * inside a zip. Suffix repeats with `_1`, `_2`, ... keeping the extension.
 */
const dedupeFilename = (filename: string, used: Set<string>): string => {
  if (!used.has(filename)) {
    used.add(filename);
    return filename;
  }
  const dot = filename.lastIndexOf(".");
  const stem = dot === -1 ? filename : filename.slice(0, dot);
  const ext = dot === -1 ? "" : filename.slice(dot);
  let i = 1;
  while (used.has(`${stem}_${i}${ext}`)) i += 1;
  const candidate = `${stem}_${i}${ext}`;
  used.add(candidate);
  return candidate;
};

export const ExportReportsModal: React.FC<ExportReportsModalProps> = ({
  open,
  onClose,
  surveys,
  format,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (open) setSelectedIds(new Set());
  }, [open]);

  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const exportableCount = surveys.filter(canExport).length;

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(
        new Set(
          surveys.filter(canExport).map((survey) => survey.parent_survey_id),
        ),
      );
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleExport = async () => {
    const selectedSurveys = surveys.filter((survey) =>
      selectedIds.has(survey.parent_survey_id),
    );
    if (selectedSurveys.length === 0) return;

    setExporting(true);
    try {
      let failures = 0;
      const exported: { blob: Blob; filename: string }[] = [];
      const succeededIds = new Set<string>();
      for (const survey of selectedSurveys) {
        const version = getExportVersion(survey);
        if (!version?.id) {
          failures += 1;
          continue;
        }
        try {
          const blob = await exportSurvey(version.id, format);
          if (blob.size === 0) {
            console.error("Empty export for survey:", survey.parent_survey_id);
            failures += 1;
            continue;
          }
          exported.push({
            blob,
            filename: surveyFilename(getSurveyTitle(survey), format),
          });
          succeededIds.add(survey.parent_survey_id);
        } catch (error) {
          console.error("Failed to export survey:", error);
          failures += 1;
        }
      }

      if (exported.length === 1) {
        triggerDownload(exported[0].blob, exported[0].filename);
      } else if (exported.length > 1) {
        const zip = new JSZip();
        const usedNames = new Set<string>();
        for (const { blob, filename } of exported) {
          zip.file(dedupeFilename(filename, usedNames), blob);
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        triggerDownload(zipBlob, "survey_exports.zip");
      }

      if (exported.length > 0) {
        toast.success(
          `Exported ${exported.length} report${exported.length !== 1 ? "s" : ""} as ${
            format === "pdf" ? "PDF" : "Markdown"
          }`,
        );
      }
      if (failures > 0) {
        toast.error(
          `Failed to export ${failures} report${failures !== 1 ? "s" : ""}`,
        );
        // Keep the modal open, but drop the surveys that succeeded so a retry
        // only re-runs the failures.
        setSelectedIds(
          (prev) => new Set([...prev].filter((id) => !succeededIds.has(id))),
        );
      } else {
        onClose();
      }
    } finally {
      setExporting(false);
    }
  };

  // Block backdrop/Escape dismissal mid-export: closing hides the modal while
  // the async loop keeps running (and reopening shows a stale "Export 0" state).
  const handleClose = () => {
    if (!exporting) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <FileDownloadIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Export Reports
          </Typography>

          <IconButton onClick={handleClose} disabled={exporting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Select the surveys you want to export as{" "}
          {format === "pdf" ? "PDF (.pdf)" : "Markdown (.md)"} files.
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {surveys.length} Survey{surveys.length !== 1 ? "s" : ""}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {selectedIds.size} of {surveys.length} selected
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      exportableCount > 0 &&
                      selectedIds.size === exportableCount
                    }
                    indeterminate={
                      selectedIds.size > 0 && selectedIds.size < exportableCount
                    }
                    disabled={exportableCount === 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>

                <TableCell>Survey Name</TableCell>
                <TableCell>Survey ID</TableCell>
                <TableCell>Version</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {surveys.map((survey) => {
                const title = getSurveyTitle(survey);
                const exportable = canExport(survey);

                return (
                  <TableRow
                    key={survey.parent_survey_id}
                    hover={exportable}
                    onClick={
                      exportable
                        ? () => handleCheckboxChange(survey.parent_survey_id)
                        : undefined
                    }
                    sx={{ cursor: exportable ? "pointer" : "default" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(survey.parent_survey_id)}
                        disabled={!exportable}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() =>
                          handleCheckboxChange(survey.parent_survey_id)
                        }
                      />
                    </TableCell>

                    <TableCell>{title}</TableCell>
                    <TableCell>{survey.parent_survey_id}</TableCell>
                    <TableCell>
                      {survey.active_version ? (
                        <Chip
                          label={`v${formatVersionNumber(survey.active_version)}`}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                      ) : survey.draft_version ? (
                        <Chip
                          label={`v${formatVersionNumber(survey.draft_version)} (draft)`}
                          variant="outlined"
                          size="small"
                          color="info"
                        />
                      ) : (
                        <Chip label="New" size="small" color="info" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          Close
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          disabled={selectedIds.size === 0 || exporting}
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Export {selectedIds.size} Report{selectedIds.size !== 1 ? "s" : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
